/**
 * OnHOW wishlist — same storage as PDP / page.wishlist (localStorage: onhow-wishlist).
 */
class OnhowWishlist {
  constructor() {
    this.storage = {
      key: 'onhow-wishlist',
      get: () => JSON.parse(localStorage.getItem(this.storage.key) || '[]'),
      set: (items) => localStorage.setItem(this.storage.key, JSON.stringify(items)),
    };
    this._lastToggleAt = 0;
    this._toastTimer = null;
    this.wishlistPageUrl = '/pages/wishlist-1';
    this.init();
  }

  init() {
    this.ensureToast();
    this.bindEvents();
    this.updateButtons();
  }

  ensureToast() {
    if (document.getElementById('skipp-wishlist-toast')) return;

    const toast = document.createElement('div');
    toast.id = 'skipp-wishlist-toast';
    toast.className = 'skipp-wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }

  bindEvents() {
    const onInteract = (e) => this.handleInteraction(e);

    document.addEventListener('click', onInteract, false);
    document.addEventListener(
      'touchend',
      (e) => {
        if (e.target.closest('.onhow-wishlist-toggle, .product-wishlist')) {
          onInteract(e);
        }
      },
      { passive: false }
    );

    document.addEventListener('shopify:section:load', () => this.updateButtons());
  }

  handleInteraction(e) {
    const toggleButton = e.target.closest('.onhow-wishlist-toggle, .product-wishlist');
    if (!toggleButton) return;

    const now = Date.now();
    if (now - this._lastToggleAt < 350) return;
    this._lastToggleAt = now;

    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }

    this.toggleProduct(toggleButton);
  }

  matchesWishlistItem(item, button) {
    if (!item || !button) return false;
    const id = button.dataset.productId;
    const title = button.dataset.productTitle;
    if (id && item.id && String(item.id) === String(id)) return true;
    if (title && item.title === title) return true;
    return false;
  }

  readProductData(button) {
    return {
      id: button.dataset.productId || '',
      handle: (button.dataset.productHandle || '').trim(),
      title: (button.dataset.productTitle || '').trim(),
      vendor: (button.dataset.productVendor || '').trim(),
      image: button.dataset.productImg || '',
      price: button.dataset.productPrice || '',
      url: button.dataset.productUrl || '',
    };
  }

  toggleProduct(button) {
    if (!button || button.classList.contains('loading')) return;

    const productData = this.readProductData(button);
    if (!productData.title && !productData.id) return;

    let wishlist = this.storage.get();
    if (!Array.isArray(wishlist)) wishlist = [];

    const index = wishlist.findIndex((item) => this.matchesWishlistItem(item, button));
    const wasInWishlist = index !== -1;

    if (!wasInWishlist) {
      wishlist.push(productData);
      this.setButtonState(button, true);
      this.showAddedToast();
    } else {
      wishlist.splice(index, 1);
      this.setButtonState(button, false);
    }

    this.storage.set(wishlist);
    this.updateWishlistCount();
    document.dispatchEvent(new CustomEvent('skipp:wishlist-updated'));

    if (!wasInWishlist) {
      button.classList.add('just-added');
      setTimeout(() => button.classList.remove('just-added'), 150);
    }

    button.classList.add('success');
    setTimeout(() => button.classList.remove('success'), 150);
  }

  showAddedToast() {
    const toast = document.getElementById('skipp-wishlist-toast');
    if (!toast) return;

    toast.innerHTML = `
      <span>Added to Wishlist ❤️</span>
      <a href="${this.wishlistPageUrl}" class="skipp-wishlist-toast__cta">View Wishlist</a>
    `;

    toast.classList.add('is-visible');

    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
    }, 3200);
  }

  setButtonState(button, isInWishlist) {
    const iconElement = button.querySelector('.onhow-wishlist-icon');
    const textElement = button.querySelector('.onhow-wishlist-text');

    if (isInWishlist) {
      button.classList.add('onhow-in-wishlist');
      button.setAttribute('aria-label', 'Remove from wishlist');
      button.setAttribute('aria-pressed', 'true');
      if (iconElement) iconElement.classList.add('onhow-wishlist-active');
      if (textElement) textElement.textContent = 'In Wishlist';
    } else {
      button.classList.remove('onhow-in-wishlist');
      button.setAttribute('aria-label', 'Add to wishlist');
      button.setAttribute('aria-pressed', 'false');
      if (iconElement) iconElement.classList.remove('onhow-wishlist-active');
      if (textElement) textElement.textContent = 'Add to Wishlist';
    }
  }

  syncButtonState(button) {
    if (!button) return;
    const wishlist = this.storage.get();
    const isInWishlist = Array.isArray(wishlist) && wishlist.some((item) => this.matchesWishlistItem(item, button));
    this.setButtonState(button, isInWishlist);
  }

  updateButtons() {
    document.querySelectorAll('.onhow-wishlist-toggle, .product-wishlist').forEach((button) => {
      this.syncButtonState(button);
    });
    this.updateWishlistCount();
  }

  updateWishlistCount() {
    const wishlist = this.storage.get();
    const count = Array.isArray(wishlist) ? wishlist.length : 0;
    document.querySelectorAll('.onhow-wishlist-count').forEach((element) => {
      element.textContent = count;
      element.style.display = count > 0 ? 'flex' : 'none';
    });
  }
}

/** Alias used by custom integrations; delegates to PDP-equivalent toggle. */
function handleWishlistToggle(buttonOrId) {
  const btn =
    typeof buttonOrId === 'object' && buttonOrId?.nodeType === 1
      ? buttonOrId
      : document.querySelector(
          `.product-wishlist[data-product-id="${buttonOrId}"], .onhow-wishlist-toggle[data-product-id="${buttonOrId}"]`
        );
  if (btn && window.onhowWishlist) {
    window.onhowWishlist.toggleProduct(btn);
  }
}

window.handleWishlistToggle = handleWishlistToggle;

function bootOnhowWishlist() {
  if (!window.onhowWishlist) {
    window.onhowWishlist = new OnhowWishlist();
  } else {
    window.onhowWishlist.updateButtons();
  }
}

document.addEventListener('DOMContentLoaded', bootOnhowWishlist);
if (document.readyState !== 'loading') {
  bootOnhowWishlist();
}
